import React, { useState, useEffect } from "react";
import { Map, GoogleApiWrapper, Marker } from "google-maps-react";
import PubNub from 'pubnub';
import { Dropdown, Select, Button, Menu } from "antd";
import { FilterButton, DropDown } from "../../assets/icons";
import location from "../../assets/icons/location.svg";
import { getProjectsData, getWorkersData } from "../../services/service";

const MapContainer = ({ ...props }) => {
  const [workerLocation, setWorkerLocation] = useState(null);
  const [mapRef, setMapRef] = useState(null);
  const [filterVisible, setFilterVisible] = useState(false);
  const [projects, setProjects] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedWorker, setSelectedWorker] = useState('');

  useEffect(() => {
    const pubnub = new PubNub({
      publishKey: 'pub-c-852c8b65-c313-48fb-a329-ebc2daf958e2',
      subscribeKey: 'sub-c-87f82bb1-b3ab-494e-9f08-e8d9e2b9d109',
      userId: 'admin-dashboard'
    });

    pubnub.subscribe({
      channels: ['worker-location'],
    });

    pubnub.addListener({
/*************  ✨ Codeium Command ⭐  *************/
      /**
       * Callback for new location message from PubNub.
       * Updates `workerLocation` state with the new location, or smoothly
       * interpolates if the change is small.
       * @param {Object} event - PubNub message event.
       * @param {Object} event.message - Message payload, containing `latitude`,
       * `longitude`, and `timestamp` properties.
       */
/******  ee44bc9f-fbdb-4af5-9e38-32351aaa106a  *******/      message: (event) => {
        console.log('Received location:', event.message);
        
        setWorkerLocation(prev => {
          // If it's first location, set it directly
          if (!prev) {
            return {
              lat: Number(event.message.latitude),
              lng: Number(event.message.longitude),
              timestamp: event.message.timestamp
            };
          }

          // If location changed significantly (more than 100m), update immediately
          const distance = calculateDistance(
            prev.lat, 
            prev.lng, 
            Number(event.message.latitude), 
            Number(event.message.longitude)
          );

          if (distance > 0.1) { // 0.1 km = 100m
            return {
              lat: Number(event.message.latitude),
              lng: Number(event.message.longitude),
              timestamp: event.message.timestamp
            };
          }

          // For small changes, smoothly interpolate
          return {
            lat: prev.lat + (Number(event.message.latitude) - prev.lat) * 0.1,
            lng: prev.lng + (Number(event.message.longitude) - prev.lng) * 0.1,
            timestamp: event.message.timestamp
          };
        });
      }
    });

    return () => {
      pubnub.unsubscribe({
        channels: ['worker-location'],
      });
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectsResponse, workersResponse] = await Promise.all([
          getProjectsData("", 1, 100, ""),
          getWorkersData("", 100, 1, "")
        ]);

        if (projectsResponse?.success) {
          setProjects(projectsResponse?.data || []);
        }
        if (workersResponse?.success) {
          setWorkers(workersResponse?.data || []);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  // Helper function to calculate distance between coordinates in kilometers
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of the earth in km
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * 
      Math.sin(dLon/2) * Math.sin(dLon/2); 
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
    return R * c;
  };

  const deg2rad = (deg) => {
    return deg * (Math.PI/180);
  };

  const onMapReady = (_, map) => {
    setMapRef(map);
  };

  // Smooth pan to new location
  useEffect(() => {
    if (mapRef && workerLocation) {
      const newCenter = new props.google.maps.LatLng(workerLocation.lat, workerLocation.lng);
      mapRef.panTo(newCenter);
    }
  }, [workerLocation, mapRef]);

  const getFilterMenu = () => (
    <Menu>
      <div className="p-4 -mt-2 -ml-3 w-[368px] rounded-[8px] bg-white filter-shadow">
        <div className="flex flex-col gap-3 font-medium text-[14px] text-[#4B5563]">
          <div>
            <p className="mb-2">Project</p>
            <Select
              placeholder="Select Project"
              value={selectedProject}
              onChange={(value) => {
                setSelectedProject(value);
              }}
              className="w-full !h-[44px] rounded-[4px] border border-[#E9E9E9]"
              suffixIcon={DropDown}
              allowClear
              style={{ 
                fontSize: '12px',
                fontFamily: 'inherit',
                color: '#4B5563',
                fontWeight:'400'
              }}
              dropdownStyle={{
                fontSize: '12px'
              }}
            >
              <Select.Option value="">All Projects</Select.Option>
              {projects.map(project => (
                <Select.Option 
                  key={project.id} 
                  value={project.id}
                  style={{ 
                    fontSize: '12px',
                    fontWeight: '400'
                  }}
                >
                  {project.title}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div>
            <p className="mb-2">Worker</p>
            <Select
              placeholder="Select Worker"
              value={selectedWorker}
              onChange={(value) => {
                setSelectedWorker(value);
              }}
              className="w-full !h-[44px] rounded-[4px] border border-[#E9E9E9]"
              suffixIcon={DropDown}
              allowClear
              style={{ 
                fontSize: '12px',
                fontFamily: 'inherit',
                color: '#4B5563',
                fontWeight:'400'
              }}
              dropdownStyle={{
                fontSize: '12px'
              }}
            >
              <Select.Option value="">All Workers</Select.Option>
              {workers.map(worker => (
                <Select.Option 
                  key={worker.id} 
                  value={worker.id}
                  style={{ 
                    fontSize: '12px',
                    fontWeight: '400'
                  }}
                >
                  {worker.username}
                </Select.Option>
              ))}
            </Select>
          </div>

          <Button
            type="submit"
            onClick={() => {
              setFilterVisible(false);
            }}
            className="bg-[#F97316] border border-[#FF6D00] w-full h-[44px] font-medium text-[13px] text-white mt-2"
          >
            Apply
          </Button>
        </div>
      </div>
    </Menu>
  );

  const getActiveFilterCount = () => {
    let count = 0;
    if (selectedProject) count++;
    if (selectedWorker) count++;
    return count;
  };

  return (
    <div className="doc-setting-input mt-5">
      <div className="flex justify-end items-center mb-4">
        <Dropdown
          overlay={getFilterMenu()}
          trigger={["click"]}
          placement="bottomRight"
          visible={filterVisible}
          onVisibleChange={(visible) => setFilterVisible(visible)}
          getPopupContainer={() => document.body}
        >
          <span className="cursor-pointer relative">
            {FilterButton}
            {getActiveFilterCount() > 0 && (
              <span className="absolute -top-2 -right-2 bg-[#F97316] text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px]">
                {getActiveFilterCount()}
              </span>
            )}
          </span>
        </Dropdown>
      </div>

      {!workerLocation ? (
        <div className="w-full h-[400px] bg-gray-50 rounded-[5px] flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-500 text-lg mb-2">Waiting for worker's location...</p>
            <p className="text-gray-400 text-sm">Location updates will appear here when available</p>
          </div>
        </div>
      ) : (
        <div style={{ width: "100%" }}>
          <Map
            google={props.google}
            onReady={onMapReady}
            initialCenter={workerLocation}
            zoom={15}
            mapTypeId="roadmap"
            style={{
              width: "96.2%",
              height: "400px",
              borderRadius: "5px",
            }}
          >
            <Marker
              position={workerLocation}
              icon={{
                url: location,
                anchor: new props.google.maps.Point(20, 20),
                scaledSize: new props.google.maps.Size(40, 40)
              }}
              title={`Last updated: ${new Date(workerLocation.timestamp).toLocaleString()}`}
              animation={null} // Remove DROP animation for smoother updates
            />
          </Map>
        </div>
      )}
      {workerLocation && (
        <div className="mt-2 text-sm text-gray-600">
          Last updated: {new Date(workerLocation.timestamp).toLocaleString()}
        </div>
      )}
    </div>
  );
};

export default GoogleApiWrapper({
  apiKey: "AIzaSyAuiAzZQ1Pbs3Ci6DmmgMztdhLjFV5a4tg",
})(MapContainer);
